import {
  Box,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  InlineStack,
} from '@customerio/pluma-components/react';
import { useDroppable } from '@dnd-kit/react';
import clsx from 'clsx';
import { Fragment } from 'react';

import { type FolderPath } from '../types';
import * as styles from './index.css';

const MAX_LIMIT_BREADCRUMBS = 4;

type BreadcrumbButtonProps = {
  folder: FolderPath;
  onClick: () => void;
  disabled: boolean;
};

function BreadcrumbButton({ folder, onClick, disabled }: BreadcrumbButtonProps) {
  const { ref, isDropTarget } = useDroppable({
    id: `breadcrumb-${folder.id}`,
    data: { folderId: folder.id },
  });

  return (
    <Button
      ref={ref}
      variant="subtle"
      size="sm"
      px="050"
      onClick={onClick}
      isDisabled={disabled}
      className={clsx(isDropTarget === true && styles.breadcrumbDropping)}
    >
      {folder.name}
    </Button>
  );
}

type FolderBreadcrumbsProps = {
  folderPaths: FolderPath[];
  currentFolderId: string | null;
  navigateToFolder: (id: string | null) => void;
  checkedAssetIds: Set<string>;
};

export function FolderBreadcrumbs({
  folderPaths,
  currentFolderId,
  navigateToFolder,
  checkedAssetIds,
}: FolderBreadcrumbsProps) {
  const isCondensed = folderPaths.length > MAX_LIMIT_BREADCRUMBS;
  const rootFolder = folderPaths[0];
  const condensedFolders = isCondensed ? folderPaths.slice(1, -2) : [];
  const visibleFolders = folderPaths.length < 2 ? [] : folderPaths.slice(isCondensed ? -2 : 1);

  const hasCheckedItems = checkedAssetIds.size > 0;

  return (
    <InlineStack gap="050" alignBlock="center" wrap="wrap">
      {rootFolder != null ? (
        <Fragment>
          <BreadcrumbButton
            folder={rootFolder}
            onClick={() => {
              if (hasCheckedItems) return;
              navigateToFolder(rootFolder.id);
            }}
            disabled={currentFolderId === rootFolder.id}
          />
          {visibleFolders.length > 0 ? <Box>/</Box> : null}
        </Fragment>
      ) : null}
      {condensedFolders.length > 0 ? (
        <Fragment>
          <DropdownMenu>
            <DropdownMenuTriggerButton
              variant="subtle"
              size="sm"
              icon="more"
              isIconOnly
              px="050"
              aria-label="Show condensed folders"
            />
            <DropdownMenuContent>
              <DropdownMenuGroup>
                {condensedFolders.map((folder) => (
                  <DropdownMenuItem
                    key={folder.id ?? 'root'}
                    onClick={() => {
                      if (hasCheckedItems) return;
                      navigateToFolder(folder.id);
                    }}
                  >
                    {folder.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
          <Box>/</Box>
        </Fragment>
      ) : null}
      {visibleFolders.map((folder, index) => (
        <Fragment key={folder.id ?? 'root'}>
          <BreadcrumbButton
            folder={folder}
            onClick={() => {
              if (hasCheckedItems) return;
              navigateToFolder(folder.id);
            }}
            disabled={currentFolderId === folder.id}
          />
          {index < visibleFolders.length - 1 ? <Box>/</Box> : null}
        </Fragment>
      ))}
    </InlineStack>
  );
}
